<template>
    <div class="bg-gray-50">
        <NotificationContainer />

        <main v-if="accountState.loaded" class="mb-8 mt-5 w-full">
            <div class="mb-4 h-auto w-full" @click="navigateToOrders">
                <BaseButtonMuted>
                    <ArrowLeftIcon class="mr-2 h-4 w-4" />
                    Back to orders
                </BaseButtonMuted>
            </div>
            <div class="grid max-w-3xl grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
                <div class="space-y-6 lg:col-span-2 lg:col-start-1">
                    <section v-if="order">
                        <OrderHeader
                            :company-logo="order?.company_logo"
                            :asset-title="order.asset_data.asset_title"
                            :company-name="order.asset_data.company_name"
                            :amount="order.total_amount"
                            :currency="order.asset_data.currency" />
                    </section>
                    <div class="flex flex-wrap gap-4 md:flex-nowrap">
                        <aside class="w-full md:w-1/3">
                            <BaseAccordion
                                :title="`${$t('portfolio.orders.contract_documents')} (${contractDocuments?.length})`"
                                class="border border-gray-300 bg-white">
                                <OrderDocumentsList
                                    v-if="contractDocuments"
                                    type="contract_document"
                                    :documents="contractDocuments" />
                            </BaseAccordion>
                            <BaseAccordion
                                :title="`${$t('portfolio.orders.reporting_documents')} (${reportingDocuments?.length})`"
                                class="border border-gray-300 bg-white">
                                <OrderDocumentsList
                                    v-if="reportingDocuments"
                                    type="reporting_document"
                                    :documents="reportingDocuments" />
                            </BaseAccordion>
                            <BaseAccordion
                                :title="`${$t('portfolio.orders.additional_documents')} (${attachments?.length})`"
                                class="border border-gray-300 bg-white">
                                <OrderDocumentsList v-if="attachments" type="attachment" :documents="attachments" />
                            </BaseAccordion>
                            <BaseAccordion
                                v-if="isCertificateEnabled()"
                                :title="`${$t('portfolio.orders.order_certificate')} (${certificate?.length})`"
                                class="border border-gray-300 bg-white">
                                <OrderDocumentsList
                                    v-if="certificate"
                                    type="order_certificate"
                                    :documents="certificate" />
                            </BaseAccordion>
                        </aside>
                        <div class="flex w-full flex-col lg:w-2/3">
                            <section>
                                <BaseSummary
                                    :title="$t('portfolio.orders.investment_details')"
                                    :items="investmentDetailsItems" />
                            </section>
                            <section
                                v-if="showCancelButton"
                                class="mt-6 rounded-lg border border-red-200 bg-red-50 p-4">
                                <p v-if="cancelDeadline !== null" class="mb-3 text-sm text-gray-700">
                                    {{ $t('portfolio.orders.cancel_period_remaining', { days: remainingCancelDays }) }}
                                </p>
                                <BaseButton class="bg-red-600 text-white hover:bg-red-700" @click="onCancelOrderClick">
                                    {{ $t('portfolio.orders.cancel_order') }}
                                </BaseButton>
                            </section>
                            <section v-if="order?.agio_disagio && order?.agio_disagio > 0" class="mt-6">
                                <div class="rounded-lg border border-gray-300 bg-white p-4">
                                    <header class="mb-4 flex h-auto w-full items-center justify-between">
                                        <h3 class="text-xl font-semibold leading-6 text-gray-700">
                                            {{ order?.agio_disagio === 1 ? 'Agio' : 'Disagio' }}
                                        </h3>
                                    </header>
                                    <section class="mb-4 flex h-auto w-full items-center justify-between">
                                        <span class="font-medium text-gray-700">
                                            {{ order?.agio_disagio_title }}
                                        </span>
                                        <span class="text-gray-700">
                                            {{ order?.agio_disagio === 1 ? '+' : '-' }}
                                            {{
                                                order?.amount &&
                                                order?.agio_disagio_rate &&
                                                formatCurrency(
                                                    (order?.amount * order?.agio_disagio_rate) / 100,
                                                    order.asset_data.currency
                                                )
                                            }}
                                        </span>
                                    </section>
                                    <p class="text-gray-700">
                                        {{ order?.agio_disagio_disclaimer }}
                                    </p>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>

                <section class="lg:col-span-1 lg:col-start-3" aria-labelledby="user-profile">
                    <AccountSidePanel />
                </section>
            </div>
        </main>
        <div v-else class="flex h-[65vh] items-center justify-center">
            <LoadingSpinner class="h-10 w-10 fill-primary" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import { i18n } from '../../localization'
import NotificationContainer from '../notification/NotificationContainer.vue'
import LoadingSpinner from '../base/LoadingSpinner.vue'
import { usePersonalAccountDataState } from '../../utilities/account/useAccountDataPersonal'
import AccountSidePanel from '../account/AccountSidePanel.vue'
import { isLegalProfile } from '../../utilities/investment-process/useUserProfileType'
import { useLegalAccountDataState } from '../../utilities/account/useAccountDataLegal'
import { OrderResponseObject, showOrder } from '../../utilities/api'
import OrderHeader from './OrderHeader.vue'
import OrderDocumentsList from './OrderDocumentsList.vue'
import BaseAccordion from '../base/BaseAccordion.vue'
import BaseSummary, { BaseSummaryItem } from '../base/BaseSummary.vue'
import { OrderStatus } from '../../typings/enums'
import moment from 'moment'
import {
    hasActiveCancelPolicy,
    isAcceptedOrderCancellable,
    isCertificateEnabled,
    computeCancelDeadline,
} from '../../utilities/usePlatformSettings'
import { cancelOrder } from '../../utilities/api/order'
import { openConfirmDialog } from '../../utilities/useDialog'
import { formatCurrency } from '../../utilities/helpers'
import BaseButtonMuted from '../base/BaseButtonMuted.vue'
import BaseButton from '../base/BaseButton.vue'
import { ArrowLeftIcon } from '@heroicons/vue/outline'

const props = defineProps<{
    orderId: string
}>()

const accountState = isLegalProfile() ? useLegalAccountDataState() : usePersonalAccountDataState()
const order = ref<OrderResponseObject | null>(null)

const contractDocuments = computed(() => {
    return order.value?.contract_data_documents.map((document) => {
        return {
            name: document.name,
            link: document.url,
        }
    })
})

const reportingDocuments = computed(() => {
    return order.value?.reporting_documents.map((document) => {
        return {
            name: document.name,
            link: document.url,
        }
    })
})

const attachments = computed(() => {
    return order.value?.attachments.map((document) => {
        return {
            name: document.name,
            link: document.url,
        }
    })
})

const certificate = computed(() => {
    return order.value?.certificate.map((document) => {
        return {
            name: document.name,
            link: document.url,
        }
    })
})

function getOrderStatusColor(status: number) {
    if (status === OrderStatus['order_status_canceled']) {
        return 'text-gray-400'
    }
    return 'text-green-600'
}

const investmentDetailsItems = computed(() => {
    if (order.value) {
        const items: BaseSummaryItem[] = [
            {
                label: i18n.global.t('portfolio.orders.order_status'),
                value: i18n.global.t(
                    `portfolio.orders.order_status_${OrderStatus[order.value.status].toLocaleLowerCase()}`
                ),
                valueColor: getOrderStatusColor(order.value.status),
            },
            {
                label: i18n.global.t('portfolio.orders.investment_date'),
                value: moment(order.value.created_on).format('DD.MM.YYYY'),
            },
            {
                label: i18n.global.t('portfolio.orders.start_of_contract'),
                value: moment(order.value.start_of_contract).format('DD.MM.YYYY'),
            },
        ]

        return items
    }
    return []
})

const cancelDeadline = computed(() => (order.value ? computeCancelDeadline(order.value) : null))

const showCancelButton = computed(
    () =>
        hasActiveCancelPolicy() &&
        (cancelDeadline.value === null || new Date() < cancelDeadline.value) &&
        order.value?.status !== OrderStatus.CANCELED &&
        (order.value?.status !== OrderStatus.ACCEPTED || isAcceptedOrderCancellable())
)

const remainingCancelDays = computed(() =>
    cancelDeadline.value ? Math.max(0, Math.ceil((cancelDeadline.value.getTime() - Date.now()) / 86400000)) : null
)

async function onCancelOrderClick() {
    if (!order.value?.order_uid) return
    const confirmed = await openConfirmDialog(
        i18n.global.t('portfolio.orders.cancel_order'),
        i18n.global.t('portfolio.orders.confirm_cancel_order'),
        { type: 'Warning' }
    )
    if (!confirmed) return
    await cancelOrder(order.value.order_uid)
    const showOrderRequest = await showOrder(props.orderId)
    order.value = showOrderRequest.data.order
}

const navigateToOrders = () => {
    window.location.href = '/order/'
}

watchEffect(async () => {
    await accountState.fetchAccountData()
    if (accountState.accountId) {
        const showOrderRequest = await showOrder(props.orderId)
        order.value = showOrderRequest.data.order
    }
})
</script>

<style scoped></style>
